import React, { useState, useEffect, useCallback } from 'react';
import { buildApiUrl } from '../../utils/apiBase.js';
import LoadingSpinner from '../common/LoadingSpinner.jsx';
import { AdminFormPageShimmer } from '../common/Shimmer.jsx';
import { useAdminDialog } from '../../contexts/AdminDialogContext.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import './AdminNotificationSettings.css';

function createWebhookId() {
  return `wh-${crypto.randomUUID().slice(0, 8)}`;
}

const emptySettings = () => ({
  slackEnabled: false,
  webhooks: [],
  eventRouting: {},
  eventOptions: {
    health_scheduler_alert: { threshold: 'warn_and_above' },
  },
});

const HEALTH_THRESHOLD_OPTIONS = [
  { value: 'warn_and_above', label: '警告・異常' },
  { value: 'down_only', label: '異常のみ' },
];

const AdminNotificationSettings = () => {
  const { showAlert } = useAdminDialog();
  const probe = useScreenProbe('notifications');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState(null);
  const [eventDefs, setEventDefs] = useState([]);
  const [settings, setSettings] = useState(emptySettings());

  const load = useCallback(async () => {
    probe.start('load');
    setLoading(true);
    try {
      const res = await fetch(buildApiUrl('/api/admin/notification-settings'), { credentials: 'include' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '取得に失敗しました');
      setSettings(data.settings || emptySettings());
      setEventDefs(data.events || []);
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      await showAlert({ message: err.message, variant: 'error', title: 'エラー' });
    } finally {
      setLoading(false);
    }
  }, [showAlert, probe]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await fetch(buildApiUrl('/api/admin/notification-settings'), {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(settings),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || '保存に失敗しました');
      setSettings(data.settings || settings);
      if (data.events) setEventDefs(data.events);
      await showAlert({ message: '通知設定を保存しました', variant: 'success', title: '保存完了' });
    } catch (err) {
      await showAlert({ message: err.message, variant: 'error', title: 'エラー' });
    } finally {
      setSaving(false);
    }
  };

  const handleTestSlack = async (webhook) => {
    setTestingId(webhook.id);
    try {
      const res = await fetch(buildApiUrl('/api/admin/notification-settings/test-slack'), {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ webhookId: webhook.id, webhookUrl: webhook.url }),
      });
      const data = await res.json();
      if (!res.ok || !data.success) {
        throw new Error(data.result?.reason || data.message || '送信に失敗しました');
      }
      await showAlert({
        message: `${webhook.label || webhook.id} へテストメッセージを送信しました`,
        variant: 'success',
        title: 'テスト成功',
      });
    } catch (err) {
      await showAlert({ message: err.message, variant: 'error', title: 'テスト失敗' });
    } finally {
      setTestingId(null);
    }
  };

  const addWebhook = () => {
    setSettings((prev) => ({
      ...prev,
      webhooks: [
        ...(prev.webhooks || []),
        { id: createWebhookId(), label: `Webhook ${(prev.webhooks?.length || 0) + 1}`, url: '', enabled: true },
      ],
    }));
  };

  const updateWebhook = (id, patch) => {
    setSettings((prev) => ({
      ...prev,
      webhooks: (prev.webhooks || []).map((wh) => (wh.id === id ? { ...wh, ...patch } : wh)),
    }));
  };

  const removeWebhook = (id) => {
    setSettings((prev) => {
      const webhooks = (prev.webhooks || []).filter((wh) => wh.id !== id);
      const eventRouting = { ...(prev.eventRouting || {}) };
      for (const eventId of Object.keys(eventRouting)) {
        eventRouting[eventId] = (eventRouting[eventId] || []).filter((whId) => whId !== id);
      }
      return { ...prev, webhooks, eventRouting };
    });
  };

  const toggleEventRoute = (eventId, webhookId) => {
    setSettings((prev) => {
      const current = prev.eventRouting?.[eventId] || [];
      const next = current.includes(webhookId)
        ? current.filter((id) => id !== webhookId)
        : [...current, webhookId];
      return {
        ...prev,
        eventRouting: { ...(prev.eventRouting || {}), [eventId]: next },
      };
    });
  };

  const setHealthAlertThreshold = (threshold) => {
    setSettings((prev) => ({
      ...prev,
      eventOptions: {
        ...(prev.eventOptions || {}),
        health_scheduler_alert: { threshold },
      },
    }));
  };

  const healthAlertThreshold = settings.eventOptions?.health_scheduler_alert?.threshold || 'warn_and_above';

  const webhooks = settings.webhooks || [];

  if (loading) {
    return <AdminFormPageShimmer fields={6} />;
  }

  return (
    <div className="admin-notification-settings admin-list-container">
      <div className="list-header">
        <div className="section-header">
          <h1>通知設定</h1>
          <p>Slack Webhook を複数登録し、イベントごとに送信先チャンネルを割り当てます。</p>
        </div>
      </div>

      <form className="admin-notification-settings-content" onSubmit={handleSave}>
        <section className="admin-notification-settings-section">
          <label className="admin-notification-settings-toggle">
            <input
              type="checkbox"
              checked={settings.slackEnabled === true}
              onChange={(e) => setSettings((p) => ({ ...p, slackEnabled: e.target.checked }))}
            />
            <span>Slack 通知を有効にする</span>
          </label>
        </section>

        <section className="admin-notification-settings-section">
          <div className="admin-notification-settings-section-head">
            <h3>Slack Webhook 一覧</h3>
            <button type="button" className="btn btn-secondary btn-sm" onClick={addWebhook}>
              Webhook を追加
            </button>
          </div>
          <p className="admin-notification-settings-section-desc">
            チャンネルごとに Incoming Webhook URL を登録します。1 つの Webhook に複数イベントを割り当てられます。
          </p>

          {webhooks.length === 0 ? (
            <p className="admin-notification-settings-empty">Webhook が未登録です。「Webhook を追加」から登録してください。</p>
          ) : (
            <div className="admin-notification-settings-webhooks">
              {webhooks.map((wh) => (
                <div key={wh.id} className="admin-notification-settings-webhook-card">
                  <div className="admin-notification-settings-webhook-grid">
                    <div className="admin-notification-settings-field">
                      <label htmlFor={`wh-label-${wh.id}`}>表示名</label>
                      <input
                        id={`wh-label-${wh.id}`}
                        type="text"
                        value={wh.label || ''}
                        onChange={(e) => updateWebhook(wh.id, { label: e.target.value })}
                        placeholder="例: #alerts"
                      />
                    </div>
                    <div className="admin-notification-settings-field admin-notification-settings-field--wide">
                      <label htmlFor={`wh-url-${wh.id}`}>Incoming Webhook URL</label>
                      <input
                        id={`wh-url-${wh.id}`}
                        type="url"
                        value={wh.url || ''}
                        onChange={(e) => updateWebhook(wh.id, { url: e.target.value })}
                        placeholder="https://hooks.slack.com/services/..."
                        autoComplete="off"
                      />
                    </div>
                  </div>
                  <div className="admin-notification-settings-webhook-actions">
                    <label className="admin-notification-settings-inline-toggle">
                      <input
                        type="checkbox"
                        checked={wh.enabled !== false}
                        onChange={(e) => updateWebhook(wh.id, { enabled: e.target.checked })}
                      />
                      <span>有効</span>
                    </label>
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm"
                      onClick={() => handleTestSlack(wh)}
                      disabled={!wh.url || testingId === wh.id}
                    >
                      {testingId === wh.id ? '送信中...' : '接続テスト'}
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-danger"
                      onClick={() => removeWebhook(wh.id)}
                    >
                      削除
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="admin-notification-settings-section">
          <h3>イベントルーティング</h3>
          <p className="admin-notification-settings-section-desc">
            各イベントを送信する Webhook にチェックを入れます。ヘルスチェック（スケジューラ）は毎回通知、
            ヘルスチェック（閾値付き）は条件を満たしたときのみ通知されます。
          </p>

          {webhooks.length === 0 ? (
            <p className="admin-notification-settings-empty">Webhook を登録するとルーティングを設定できます。</p>
          ) : (
            <div className="admin-notification-settings-routing-wrap">
              <table className="admin-notification-settings-routing">
                <thead>
                  <tr>
                    <th>イベント</th>
                    {webhooks.map((wh) => (
                      <th key={wh.id}>{wh.label || wh.id}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {eventDefs.map((ev) => (
                    <tr key={ev.id}>
                      <td>
                        <div className="admin-notification-settings-event-label">{ev.label}</div>
                        {ev.description && (
                          <div className="admin-notification-settings-event-desc">{ev.description}</div>
                        )}
                        {ev.optionSchema === 'health_threshold' && (
                          <fieldset className="admin-notification-settings-event-options">
                            <legend className="admin-notification-settings-event-options-legend">通知閾値</legend>
                            <div className="admin-notification-settings-event-options-radios">
                              {HEALTH_THRESHOLD_OPTIONS.map((opt) => (
                                <label key={opt.value} className="admin-notification-settings-radio">
                                  <input
                                    type="radio"
                                    name="health_scheduler_alert_threshold"
                                    value={opt.value}
                                    checked={healthAlertThreshold === opt.value}
                                    onChange={() => setHealthAlertThreshold(opt.value)}
                                  />
                                  <span>{opt.label}</span>
                                </label>
                              ))}
                            </div>
                          </fieldset>
                        )}
                      </td>
                      {webhooks.map((wh) => (
                        <td key={`${ev.id}-${wh.id}`} className="admin-notification-settings-routing-check">
                          <input
                            type="checkbox"
                            aria-label={`${ev.label} → ${wh.label}`}
                            checked={(settings.eventRouting?.[ev.id] || []).includes(wh.id)}
                            onChange={() => toggleEventRoute(ev.id, wh.id)}
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="admin-notification-settings-actions">
          <button type="submit" className="btn btn-primary" disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminNotificationSettings;
